import {ApplicationStatus} from 'BOSTools/models/ApplicationStatus';
import {CaseAgentNumber} from 'BOSTools/models/CaseAgentNumber';
import {CaseName} from 'BOSTools/models/CaseName';
import {GrandfatheringData} from 'BOSTools/models/GrandfatherData';
import {ValidGuid} from 'BOSTools/models/Guid';
import {LifeDetail} from 'BOSTools/models/LifeDetail';
import {SicCode} from 'BOSTools/models/SicCode';
import {Async, AsyncPage, Empty} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {createAction} from 'Shared/utilities/actionCreator';

export enum BOSToolsActionTypes {
  GET_APPLICATION_STATUS_BY_SCENE_ID = 'bostools/GET_APPLICATION_STATUS_BY_SCENE_ID',
  CHANGE_APPLICATION_STATUS = 'bostools/CHANGE_APPLICATION_STATUS',
  UPDATE_APPLICATION_STATUS = 'bostools/UPDATE_APPLICATION_STATUS',
  GET_CASE_AGENT_NUMBERS_BY_CASE_NUMBER = 'bostools/GET_CASE_AGENT_NUMBERS_BY_CASE_NUMBER',
  CHANGE_CASE_AGENT_NUMBERS = 'bostools/CHANGE_CASE_AGENT_NUMBERS',
  UPDATE_CASE_AGENT_NUMBERS = 'bostools/UPDATE_CASE_AGENT_NUMBERS',
  GET_GRANDFATHERING_DATA_REPORT = 'bostools/GET_GRANDFATHERING_DATA_REPORT',
  CHANGE_GRANDFATHERING_DATA_REPORT = 'bostools/CHANGE_GRANDFATHERING_DATA_REPORT',
  GET_SIC_CODES_BY_CASE_NUMBER = 'bostools/GET_SIC_CODES_BY_CASE_NUMBER',
  CHANGE_SIC_CODES = 'bostools/CHANGE_SIC_CODES',
  UPDATE_SIC_CODE = 'bostools/UPDATE_SIC_CODE',
  GET_CASE_NAMES_BY_CASE_NUMBER = 'bostools/GET_CASE_NAMES_BY_CASE_NUMBER',
  CHANGE_CASE_NAMES = 'bostools/CHANGE_CASE_NAMES',
  UPDATE_CASE_NAME = 'bostools/UPDATE_CASE_NAME',
  GET_LIFE_DETAILS_BY_ACCOUNT_NUMBER = 'bostools/GET_LIFE_DETAILS_BY_ACCOUNT_NUMBER',
  CHANGE_LIFE_DETAILS = 'bostools/CHANGE_LIFE_DETAILS',
  UPDATE_WRITTEN_TO_L70_FLAG = 'bostools/UPDATE_WRITTEN_TO_L70_FLAG',
}

export const getApplicationStatusBySceneID = (sceneID: ValidGuid) =>
  createAction(BOSToolsActionTypes.GET_APPLICATION_STATUS_BY_SCENE_ID, {sceneID});
export const changeApplicationStatus = (applicationStatus: Async<ApplicationStatus>) =>
  createAction(BOSToolsActionTypes.CHANGE_APPLICATION_STATUS, {applicationStatus});
export const updateApplicationStatus = (sceneID: ValidGuid, status: number) =>
  createAction(BOSToolsActionTypes.UPDATE_APPLICATION_STATUS, {sceneID, status});

export const getCaseAgentNumbersByCaseNumber = (caseNumber: string) =>
  createAction(BOSToolsActionTypes.GET_CASE_AGENT_NUMBERS_BY_CASE_NUMBER, {caseNumber});
export const changeCaseAgentNumbers = (caseAgentNumbers: Async<CaseAgentNumber[]>) =>
  createAction(BOSToolsActionTypes.CHANGE_CASE_AGENT_NUMBERS, {caseAgentNumbers});
export const updateCaseAgentNumber = (caseAgentNumber: CaseAgentNumber) =>
  createAction(BOSToolsActionTypes.UPDATE_CASE_AGENT_NUMBERS, {caseAgentNumber});

export const getSicCodesByCaseNumber = (caseNumber: string) =>
  createAction(BOSToolsActionTypes.GET_SIC_CODES_BY_CASE_NUMBER, {caseNumber});
export const changeSicCodes = (sicCodes: Async<SicCode[]>) =>
  createAction(BOSToolsActionTypes.CHANGE_SIC_CODES, {sicCodes});
export const updateSicCode = (sicCode: SicCode) => createAction(BOSToolsActionTypes.UPDATE_SIC_CODE, {sicCode});

export const getCaseNamesByCaseNumber = (caseNumber: string) =>
  createAction(BOSToolsActionTypes.GET_CASE_NAMES_BY_CASE_NUMBER, {caseNumber});
export const changeCaseNames = (caseNames: Async<CaseName[]>) =>
  createAction(BOSToolsActionTypes.CHANGE_CASE_NAMES, {caseNames});
export const updateCaseName = (caseName: CaseName) => createAction(BOSToolsActionTypes.UPDATE_CASE_NAME, {caseName});

export const getLifeDetailsByAccountNumber = (accountNumber: string) =>
  createAction(BOSToolsActionTypes.GET_LIFE_DETAILS_BY_ACCOUNT_NUMBER, {accountNumber});
export const changeLifeDetails = (lifeDetails: Async<LifeDetail[]>) =>
  createAction(BOSToolsActionTypes.CHANGE_LIFE_DETAILS, {lifeDetails});
export const updateWrittenToL70Flag = (lifeDetail: LifeDetail) =>
  createAction(BOSToolsActionTypes.UPDATE_WRITTEN_TO_L70_FLAG, {lifeDetail});

export const getGrandfatheringData = (pageOption?: Page<GrandfatheringData>) =>
  createAction(BOSToolsActionTypes.GET_GRANDFATHERING_DATA_REPORT, {pageOption});
export const changeGrandfatheringData = (grandfatheringData: AsyncPage<Page<GrandfatheringData>>) =>
  createAction(BOSToolsActionTypes.CHANGE_GRANDFATHERING_DATA_REPORT, {grandfatheringData});

type BOSToolsActions =
  | ReturnType<typeof changeApplicationStatus>
  | ReturnType<typeof changeCaseAgentNumbers>
  | ReturnType<typeof changeGrandfatheringData>
  | ReturnType<typeof changeSicCodes>
  | ReturnType<typeof changeCaseNames>
  | ReturnType<typeof changeLifeDetails>;

type BOSToolsState = {
  applicationStatus: Async<ApplicationStatus>;
  caseAgentNumbers: Async<CaseAgentNumber[]>;
  grandfatheringData: AsyncPage<Page<GrandfatheringData>>;
  sicCodes: Async<SicCode[]>;
  caseNames: Async<CaseName[]>;
  lifeDetails: Async<LifeDetail[]>;
};

export const DEFAULT_STATE = {
  applicationStatus: new Empty(),
  caseAgentNumber: new Empty(),
  sicCodes: new Empty(),
  caseAgentNumbers: new Empty(),
  grandfatheringData: new Empty(),
  caseNames: new Empty(),
  lifeDetails: new Empty(),
};

export default (state: BOSToolsState = DEFAULT_STATE, action: BOSToolsActions): BOSToolsState => {
  switch (action.type) {
    case BOSToolsActionTypes.CHANGE_APPLICATION_STATUS:
      return {
        ...state,
        applicationStatus: action.payload.applicationStatus,
      };
    case BOSToolsActionTypes.CHANGE_CASE_AGENT_NUMBERS:
      return {
        ...state,
        caseAgentNumbers: action.payload.caseAgentNumbers,
      };
    case BOSToolsActionTypes.CHANGE_GRANDFATHERING_DATA_REPORT:
      return {
        ...state,
        grandfatheringData: action.payload.grandfatheringData,
      };
    case BOSToolsActionTypes.CHANGE_SIC_CODES:
      return {
        ...state,
        sicCodes: action.payload.sicCodes,
      };
    case BOSToolsActionTypes.CHANGE_CASE_NAMES:
      return {
        ...state,
        caseNames: action.payload.caseNames,
      };
    case BOSToolsActionTypes.CHANGE_LIFE_DETAILS:
      return {
        ...state,
        lifeDetails: action.payload.lifeDetails,
      };
    default:
      return state;
  }
};
